import React, { useState, useEffect } from "react";
import { useLang } from "@/lib/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, Lock } from "lucide-react";

const initialForm = {
  name: "",
  email: "",
  age: "",
  current_situation: "",
  objective: "",
  weekly_time: "",
  what_holds_back: "",
  why_mtk: "",
};

export default function ApplicationForm() {
  const { t } = useLang();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [fee, setFee] = useState({ amount: 100, currency: "eur" });

  useEffect(() => {
    api.get("/config/fee").then(({ data }) => setFee(data)).catch(() => {});
  }, []);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    try {
      const payload = { ...form, age: parseInt(form.age, 10) };
      const { data: app } = await api.post("/applications", payload);
      let checkout;
      try {
        const res = await api.post("/applications/checkout", {
          application_id: app.id,
          origin_url: window.location.origin,
        });
        checkout = res.data;
      } catch (err) {
        // Cleanup orphan application (best-effort) — only if checkout creation failed
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        setLoading(false);
        return;
      }
      toast.success(t.form.paying);
      try {
        localStorage.setItem("mtk_last_app_id", app.id);
      } catch (_) {}
      window.location.href = checkout.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  const inputCls =
    "rounded-none bg-mtk-surface border border-mtk-border text-white placeholder:text-zinc-600 focus-visible:ring-1 focus-visible:ring-mtk-gold focus-visible:ring-offset-0 focus-visible:border-mtk-gold/60 h-12";
  const labelCls = "font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-400";

  const submitLabel = t.form.paySubmit.replace("{amount}", String(fee.amount).replace(".0", ""));

  return (
    <div data-testid="application-form-wrapper">
      {/* Fee notice */}
      <div className="mb-8 border border-mtk-gold/30 bg-mtk-gold/[0.04] p-5 md:p-6 flex gap-4" data-testid="fee-notice">
        <ShieldCheck className="w-6 h-6 text-mtk-gold flex-shrink-0 mt-0.5" strokeWidth={1.5} />
        <div>
          <div className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-mtk-gold">
            {t.form.feeNoticeTitle}
          </div>
          <p className="mt-2 text-zinc-300 text-sm leading-relaxed">{t.form.feeNoticeCopy}</p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="space-y-6" data-testid="application-form">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="name" className={labelCls}>{t.form.labels.name}</Label>
            <Input
              id="name"
              data-testid="form-name"
              required
              value={form.name}
              onChange={onChange("name")}
              placeholder={t.form.placeholders.name}
              className={inputCls}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className={labelCls}>{t.form.labels.email}</Label>
            <Input
              id="email"
              data-testid="form-email"
              type="email"
              required
              value={form.email}
              onChange={onChange("email")}
              placeholder={t.form.placeholders.email}
              className={inputCls}
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="age" className={labelCls}>{t.form.labels.age}</Label>
            <Input
              id="age"
              data-testid="form-age"
              type="number"
              min="14"
              max="99"
              required
              value={form.age}
              onChange={onChange("age")}
              placeholder={t.form.placeholders.age}
              className={inputCls}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="weekly_time" className={labelCls}>{t.form.labels.time}</Label>
            <Input
              id="weekly_time"
              data-testid="form-weekly-time"
              required
              value={form.weekly_time}
              onChange={onChange("weekly_time")}
              placeholder={t.form.placeholders.time}
              className={inputCls}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="situation" className={labelCls}>{t.form.labels.situation}</Label>
          <Textarea
            id="situation"
            data-testid="form-current_situation"
            required
            rows={4}
            value={form.current_situation}
            onChange={onChange("current_situation")}
            placeholder={t.form.placeholders.situation}
            className={`${inputCls} h-auto py-3`}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="objective" className={labelCls}>{t.form.labels.objective}</Label>
          <Textarea
            id="objective"
            data-testid="form-objective"
            required
            rows={3}
            value={form.objective}
            onChange={onChange("objective")}
            placeholder={t.form.placeholders.objective}
            className={`${inputCls} h-auto py-3`}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="block" className={labelCls}>{t.form.labels.block}</Label>
          <Textarea
            id="block"
            data-testid="form-what_holds_back"
            required
            rows={3}
            value={form.what_holds_back}
            onChange={onChange("what_holds_back")}
            placeholder={t.form.placeholders.block}
            className={`${inputCls} h-auto py-3`}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="why" className={labelCls}>{t.form.labels.why}</Label>
          <Textarea
            id="why"
            data-testid="form-why_mtk"
            required
            rows={3}
            value={form.why_mtk}
            onChange={onChange("why_mtk")}
            placeholder={t.form.placeholders.why}
            className={`${inputCls} h-auto py-3`}
          />
        </div>

        <Button
          type="submit"
          disabled={loading}
          data-testid="application-submit-btn"
          className="w-full rounded-none bg-mtk-gold text-mtk-bg hover:bg-mtk-gold-light font-bold uppercase tracking-[0.25em] text-sm h-14 disabled:opacity-60"
        >
          {loading ? t.form.paying : submitLabel}
        </Button>

        <div className="flex items-center justify-center gap-2 pt-2">
          <Lock className="w-3.5 h-3.5 text-zinc-500" />
          <span className="font-mono-mtk text-[10px] tracking-[0.3em] uppercase text-zinc-500">
            STRIPE · SECURE PAYMENT
          </span>
        </div>
      </form>
    </div>
  );
}
